/**
 * Where the native bridge keeps its auth credentials (P1-12, architecture §J [C14]).
 *
 * On Android the values are sealed by the local SecureTokenStore plugin
 * (frontend/android/.../securestore/SecureTokenStorePlugin.java): AES-256-GCM
 * under a non-exportable Android Keystore key, ciphertext only on disk.
 *
 * Before P1-12 the session JWT was kept in plaintext in two places: the
 * WebView's localStorage (inside the Zustand 'auth-storage' blob) and
 * @capacitor/preferences (an ordinary SharedPreferences file). On first launch
 * of this build, auth-token-persistence.ts moves an existing token into the
 * vault and then deletes those copies.
 *
 * Fallback: a shell without the plugin (iOS, which is not shipped; or a shell
 * that predates it, which the native fingerprint keeps from receiving this JS
 * over the air) uses @capacitor/preferences, i.e. the previous behaviour, so
 * the app still works. It never falls back to localStorage.
 *
 * The web console has no native store: there the token stays in the Zustand
 * blob in localStorage, as before, and this module is not used.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Entry names live in a dependency-free module so code on the API client's
// import path (device-session-refresh.ts) can name them without loading plugins.
export { DEVICE_REFRESH_VAULT_KEYS, VAULT_KEYS } from './vault-keys';

export interface TokenVault {
  /** 'keystore' when values are sealed by the Android Keystore. */
  readonly kind: 'keystore' | 'preferences' | 'memory';
  get(key: string): Promise<string | null>;
  /** Resolves only once the value is durably written. */
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

interface SecureTokenStorePlugin {
  isAvailable(): Promise<{ available: boolean }>;
  get(options: { key: string }): Promise<{ value: string | null }>;
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
}

const SecureTokenStore = registerPlugin<SecureTokenStorePlugin>('SecureTokenStore');

export function createKeystoreVault(plugin: SecureTokenStorePlugin = SecureTokenStore): TokenVault {
  return {
    kind: 'keystore',
    async get(key) {
      const { value } = await plugin.get({ key });
      return typeof value === 'string' && value.length > 0 ? value : null;
    },
    async set(key, value) {
      await plugin.set({ key, value });
    },
    async remove(key) {
      await plugin.remove({ key });
    },
  };
}

/** Prefix keeps fallback entries apart from the legacy 'auth-storage' Preferences key. */
const PREFERENCES_PREFIX = 'vault:';

export function createPreferencesVault(): TokenVault {
  return {
    kind: 'preferences',
    async get(key) {
      const { value } = await Preferences.get({ key: PREFERENCES_PREFIX + key });
      return value && value.length > 0 ? value : null;
    },
    async set(key, value) {
      await Preferences.set({ key: PREFERENCES_PREFIX + key, value });
    },
    async remove(key) {
      await Preferences.remove({ key: PREFERENCES_PREFIX + key });
    },
  };
}

/** For tests. */
export function createMemoryVault(initial: Record<string, string> = {}): TokenVault & { data: Map<string, string> } {
  const data = new Map(Object.entries(initial));
  return {
    kind: 'memory',
    data,
    async get(key) {
      return data.get(key) ?? null;
    },
    async set(key, value) {
      data.set(key, value);
    },
    async remove(key) {
      data.delete(key);
    },
  };
}

let resolved: Promise<TokenVault> | null = null;

/**
 * The vault for this platform, resolved once. Android with the plugin present
 * gets the Keystore; anything else native gets Preferences.
 */
export function getNativeTokenVault(): Promise<TokenVault> {
  if (!resolved) {
    resolved = (async () => {
      if (Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('SecureTokenStore')) {
        try {
          const { available } = await SecureTokenStore.isAvailable();
          if (available) return createKeystoreVault();
        } catch {
          // Keystore unusable on this device: fall through.
        }
      }
      return createPreferencesVault();
    })();
  }
  return resolved;
}

/** For tests. */
export function __resetNativeTokenVaultForTests(): void {
  resolved = null;
}
